import { ApiMutualFundAssetRead, ApiSegregatedPortfolioAssetRead } from 'api/rtk-api'
import { useState } from 'react'

import {
  AssetInformationModal,
  Attribute,
  DateDisplay,
  LinkButton
} from '../../../../../../components/core-v2'
import { unpascal } from '../../../../util'
import { LeverageConstantProportionState } from '../../../04-strategy/state/leverage-constant-proportion'
import { LeverageVolControlState } from '../../../04-strategy/state/leverage-vol-control'
import { RiskManagedState } from '../../../04-strategy/state/risk-managed'
import { ProtectionState, StrategyState } from '../../../04-strategy/state/strategy-slice'
import { ApprovalBox } from '../../molecules/approval-box'
import { ConfirmationBox } from '../../molecules/confirmation-box'
import { ApprovalProps } from './types'

type Props = ApprovalProps & {
  strategy: StrategyState
}

function Delta1Summary() {
  return <Attribute name="Type">Delta One</Attribute>
}

function LeverageConstantProportionSummary({ leverageRatio }: LeverageConstantProportionState) {
  return (
    <>
      <Attribute name="Type">Leverage (Constant Proportion)</Attribute>
      <Attribute name="Leverage ratio">{leverageRatio?.value}</Attribute>
    </>
  )
}

function LeverageVolControlSummary({
  leverageRatio,
  protectionAsset,
  targetVolatility
}: LeverageVolControlState) {
  const [inspectingAsset, setInspectingAsset] = useState<
    ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead
  >()
  return (
    <>
      <Attribute name="Type">Leverage (Volatility Control)</Attribute>
      <Attribute name="Leverage ratio">{leverageRatio?.value}</Attribute>
      <Attribute name="Target volatility (%)">{targetVolatility.value}</Attribute>
      <AssetInformationModal
        assetId={inspectingAsset?.assetId}
        isOpen={Boolean(inspectingAsset)}
        onClose={() => setInspectingAsset(undefined)}
      />
      {protectionAsset && (
        <Attribute key={protectionAsset.assetId} name="Protection asset">
          <LinkButton underline onClick={() => setInspectingAsset(protectionAsset as any)}>
            {protectionAsset?.name}
          </LinkButton>
        </Attribute>
      )}
    </>
  )
}

function RiskManagedSummary({ protectionAsset, targetVolatility }: RiskManagedState) {
  const [inspectingAsset, setInspectingAsset] = useState<
    ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead
  >()
  return (
    <>
      <Attribute name="Type">Risk Managed</Attribute>
      <Attribute name="Target volatility (%)">{targetVolatility.value}</Attribute>
      <AssetInformationModal
        assetId={inspectingAsset?.assetId}
        isOpen={Boolean(inspectingAsset)}
        onClose={() => setInspectingAsset(undefined)}
      />
      {protectionAsset && (
        <Attribute key={protectionAsset.assetId} name="Protection asset">
          <LinkButton underline onClick={() => setInspectingAsset(protectionAsset as any)}>
            {protectionAsset?.name}
          </LinkButton>
        </Attribute>
      )}
    </>
  )
}

function ProtectionContinuousSummary({
  continuous,
  isApplyVolControl,
  isBankGuarantee
}: ProtectionState) {
  const [inspectingAsset, setInspectingAsset] = useState<
    ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead
  >()
  return (
    <>
      <Attribute name="Type">Protection (Continuous)</Attribute>
      <Attribute name="Bank protection">{isBankGuarantee ? 'Yes' : 'No'}</Attribute>
      <Attribute name="Protection level">{continuous?.protectionLevel?.value}</Attribute>
      <Attribute name="Volatility control">{isApplyVolControl ? 'Yes' : 'No'}</Attribute>
      {isApplyVolControl && (
        <Attribute name="Target volatility (%)">{continuous?.targetVolatility?.value}</Attribute>
      )}
      <Attribute name="High watermark">{unpascal(continuous.highWaterMarkFrequency)}</Attribute>
      <Attribute name="Leverage ratio">{continuous?.leverageRatio?.value}</Attribute>
      <AssetInformationModal
        assetId={inspectingAsset?.assetId}
        isOpen={Boolean(inspectingAsset)}
        onClose={() => setInspectingAsset(undefined)}
      />
      {continuous?.protectionAsset && (
        <Attribute key={continuous.protectionAsset.assetId} name="Protection asset">
          <LinkButton
            underline
            onClick={() => setInspectingAsset(continuous.protectionAsset as any)}
          >
            {continuous.protectionAsset?.name}
          </LinkButton>
        </Attribute>
      )}
    </>
  )
}

function ProtectionTermSummary({ isApplyVolControl, isBankGuarantee, term }: ProtectionState) {
  const [inspectingAsset, setInspectingAsset] = useState<
    ApiMutualFundAssetRead | ApiSegregatedPortfolioAssetRead
  >()
  return (
    <>
      <Attribute name="Type">Protection (Term)</Attribute>
      <Attribute name="Bank guarantee">{isBankGuarantee ? 'Yes' : 'No'}</Attribute>
      <Attribute name="Protection level">{term?.protectionLevel?.value}</Attribute>
      <Attribute name="Volatility control">{isApplyVolControl ? 'Yes' : 'No'}</Attribute>
      {isApplyVolControl && (
        <Attribute name="Target volatility (%)">{term?.targetVolatility?.value}</Attribute>
      )}
      <Attribute name="High watermark">{unpascal(term.highWaterMarkFrequency)}</Attribute>
      <Attribute name="Leverage ratio">{term?.leverageRatio?.value}</Attribute>
      <Attribute name="Maturity date">
        <DateDisplay value={term.maturityDate.value} />
      </Attribute>
      <AssetInformationModal
        assetId={inspectingAsset?.assetId}
        isOpen={Boolean(inspectingAsset)}
        onClose={() => setInspectingAsset(undefined)}
      />
      {term?.protectionAsset && (
        <Attribute key={term.protectionAsset.assetId} name="Protection asset">
          <LinkButton underline onClick={() => setInspectingAsset(term?.protectionAsset as any)}>
            {term?.protectionAsset?.name}
          </LinkButton>
        </Attribute>
      )}
    </>
  )
}

export function StrategyAttributes({ strategy }: Props) {
  switch (strategy.type) {
    case 'delta1': {
      return <Delta1Summary />
    }

    case 'leverage': {
      return strategy.leverage.type === 'constant-proportion' ? (
        <LeverageConstantProportionSummary {...strategy.leverage.constantProportion} />
      ) : (
        <LeverageVolControlSummary {...strategy.leverage.volControl} />
      )
    }

    case 'riskmanaged':
      return <RiskManagedSummary {...strategy.riskManaged} />

    case 'protection':
      return strategy.protection.type === 'continuous' ? (
        <ProtectionContinuousSummary {...strategy.protection} />
      ) : (
        <ProtectionTermSummary {...strategy.protection} />
      )

    default:
      return <div>Invalid strategy</div>
  }
}

export const ApproveStrategy = ({
  isApproved,
  onChangeIsApproved,
  onClickEdit,
  strategy
}: Props) => (
  <ApprovalBox isApproved={isApproved} title="Strategy">
    <StrategyAttributes isApproved={isApproved} strategy={strategy} />

    <ConfirmationBox
      elementTitle="Strategy"
      isApproved={isApproved}
      isAre="is"
      onChangeIsApproved={onChangeIsApproved}
      onClickEdit={onClickEdit}
    />
  </ApprovalBox>
)
